// ─── Response type ───────────────────────────────────────────────────────────────

export interface CustoDetalhado {
  volume: number;
  tarifa: {
    comImposto: number;
    semImposto: number;
  };
  custoImpostos: {
    icms: number;
    pis: number;
    cofins: number;
  };
  custoTotal: {
    comImposto: number;
    semImposto: number;
  };
}

export interface CustoTotalDetalhado {
  custoImpostos: {
    icms: number;
    pis: number;
    cofins: number;
  };
  custoTotal: {
    comImposto: number;
    semImposto: number;
  };
}

export interface CustoConexaoOutput {
  consumoPonta: CustoDetalhado;
  consumoForaPonta: CustoDetalhado;
  demandaForaPonta: CustoDetalhado;
  demandaForaPontaSemDesconto: CustoDetalhado;
  demandaForaPontaIsenta: CustoDetalhado;
  demandaForaPontaUltrapassagem: CustoDetalhado;
  demandaPonta: CustoDetalhado;
  demandaPontaSemDesconto: CustoDetalhado;
  demandaPontaIsenta: CustoDetalhado;
  demandaPontaUltrapassagem: CustoDetalhado;
  custoConexao: CustoTotalDetalhado;
}

export interface CustoEnergiaOutput {
  energiaForaPonta: CustoDetalhado;
  energiaPonta: CustoDetalhado;
  custoEnergia: CustoTotalDetalhado;
}

export interface CustoEnergiaLivreOutput {
  energiaFaturavel: CustoDetalhado;
  energiaExcedente: CustoDetalhado;
  custoEnergia: CustoTotalDetalhado;
}

export interface ValoresBandeirasTarifariasOutput {
  custoDetalhado: {
    verde: CustoDetalhado;
    amarela: CustoDetalhado;
    vermelhaP1: CustoDetalhado;
    vermelhaP2: CustoDetalhado;
  };
  custoTotal: {
    verde: number;
    amarela: number;
    vermelhaP1: number;
    vermelhaP2: number;
  };
}

export interface EncargosSimulacaoOutput {
  taxaCovid: CustoDetalhado;
  taxaEscassez: CustoDetalhado;
  encargoSetorial: CustoDetalhado;
  custos: CustoTotalDetalhado;
}

export interface CustoBeneficioTarifarioOutput {
  demandaForaPonta: CustoDetalhado;
  demandaPonta: CustoDetalhado;
  consumoPonta: CustoDetalhado;
  demandaForaPontaIsenta: CustoDetalhado;
  demandaPontaIsenta: CustoDetalhado;
  beneficioTarifarioSemImposto: number;
  beneficioTarifarioComImposto: number;
  custos: CustoTotalDetalhado;
}

export interface IResponseCativo
  extends CustoConexaoOutput,
    CustoEnergiaOutput {
  custoGerador: CustoDetalhado;
  valoresBandeiras: ValoresBandeirasTarifariasOutput;
  custos: CustoTotalDetalhado;
}

export interface IResponseGD extends CustoConexaoOutput, CustoEnergiaOutput {
  custoGerador: CustoDetalhado;
  consumoForaPontaInjetada: CustoDetalhado;
  consumoPontaInjetada: CustoDetalhado;
  energiaForaPontaInjetada: CustoDetalhado;
  energiaPontaInjetada: CustoDetalhado;
  demandaUsina: CustoDetalhado;
  valoresBandeiras: ValoresBandeirasTarifariasOutput;
  custos: CustoTotalDetalhado;
}

export interface IResponseML
  extends CustoConexaoOutput,
    CustoEnergiaLivreOutput {
  beneficioTarifario: CustoBeneficioTarifarioOutput;
  encargos: EncargosSimulacaoOutput;
  operacional: number;
  custos: CustoTotalDetalhado;
}

export interface IResponseAPE
  extends CustoConexaoOutput,
    CustoEnergiaLivreOutput {
  consumoForaPontaAGP: CustoDetalhado;
  consumoPontaAGP: CustoDetalhado;
  demandaUsina: CustoDetalhado;
  tfsee: CustoDetalhado;
  encargos: EncargosSimulacaoOutput;
  beneficioTarifario: CustoBeneficioTarifarioOutput;
  operacional: number;
  custos: CustoTotalDetalhado;
}

/**
 * Interface para itens de economia com dados anuais
 */
export interface EconomyDataItem {
  ano: string;
  firstTotal: number;
  secondTotal: number;
  economia: number;
  economiaPct: number;
  economiaAnual: number;
}

/**
 * Resultado padronizado dos cálculos de indicadores financeiros (TIR, VPL, Payback)
 */
export interface FinancialIndicatorsResult {
  tir: number;
  vpl: number;
  vplAnual: number[];
  selic: number;
  taxaAtratividade: number;
  payBackAnual: number;
  payBackMensal: number;
  paybackMensalExtenso: string;
}

export interface EconomyTargetPrice {
  precoEnergia: number;
  economia: number;
  economiaPct: number;
}

export interface EconomyCompareResult extends FinancialIndicatorsResult {
  firstSimulation: IResponseCativo | IResponseML | IResponseGD | IResponseAPE;
  secondSimulation: IResponseCativo | IResponseML | IResponseGD | IResponseAPE;
  economyData: EconomyDataItem[];
  economyAnual: number[];
  economyAccumulated: number[];
  custoGestao: number;
  targetPrices: EconomyTargetPrice[] | null;
  bess: BessSimulationResult | null;
}

// ─── BESS (simulação horária de bateria) ─────────────────────────────────────

export type BessSubmercado = "SUDESTE" | "SUL" | "NORDESTE" | "NORTE";

export interface BessParametros {
  pontoMedicao: string;
  submercado: BessSubmercado;
  mesReferencia: string; // 'MM/YYYY'
  potenciaBateria: number;
  energiaMaximaArmazenada: number;
  capacidadeUtilKwh: number;
  rteBateria: number;
  dod: number;
  demandaContratada: number;
  permiteInjecao: boolean;
  horasPonta: number[];
  custoCargaRedeKwh: number;
  ganhoDescargaKwh: number;
  precoEnergia: number;
}

export interface BessHoraResultado {
  data: string; // 'YYYY-MM-DD'
  hora: number; // 1–24
  consumoOriginal: number;
  geracaoOriginal: number;
  consumoAjustado: number;
  geracaoAjustada: number;
  consumoFinal: number;
  pld: number;
  acao: "carregar" | "descarregar" | "ocioso";
  fonteCarga: "rede" | "solar" | "mista" | null;
  soc: number;
  energiaCargaAc: number;
  energiaCargaRedeAc: number;
  energiaCargaSolarAc: number;
  energiaDescargaDc: number;
  energiaEntregueAc: number;
  energiaDescargaConsumoAc: number;
  energiaInjetadaKwh: number;
  custoCarga: number;
  ganhoDescarga: number;
  mcp: number;
  mcpSemBess: number;
  mcpDelta: number;
  lucro: number;
}

// Espelha o BessResumoDiario do backend (bess.types.ts) — o detalhamento por
// fonte (rede/solar) e a injeção diária só existem na série horária.
export interface BessResumoDiario {
  data: string;
  consumoOriginal: number;
  geracaoOriginal: number;
  consumoAjustado: number;
  geracaoAjustada: number;
  consumoFinal: number;
  energiaCargaAc: number;
  energiaDescargaDc: number;
  energiaEntregueAc: number;
  custoCarga: number;
  ganhoDescarga: number;
  mcp: number;
  mcpSemBess: number;
  mcpDelta: number;
  lucro: number;
}

export interface BessResumoGeral {
  energiaCarregadaAcKwh: number;
  energiaCarregadaRedeKwh: number;
  energiaCarregadaSolarKwh: number;
  energiaDescarregadaDcKwh: number;
  energiaEntregueAcKwh: number;
  energiaInjetadaKwh: number;
  ciclosEquivalentes: number;
  custoCargaRede: number;
  custoOportunidadeSolar: number;
  ganhoDescarga: number;
  mcpTotal: number;
  mcpTotalSemBess: number;
  mcpDelta: number;
  // Ganho mensal (ano 0) de benefício tarifário que a bateria gera ao abater o
  // consumo de ponta. Independente do mcpDelta; 0 quando não há bateria.
  beneficioTarifarioDelta: number;
  medias: {
    consumoMedioMwm: number;
    geracaoMedioMwm: number;
    consumoMedioSemBessMwm: number;
    geracaoMedioSemBessMwm: number;
  };
  lucroTotal: number;
  cargaIncompleta: boolean;
  kwhNaoCarregados: number;
  horasFaltantes: number;
  pldAnoUtilizado: number;
}

export interface BessAjuste {
  consumoForaPontaDelta: number;
  consumoPontaDelta: number;
  geracaoDelta: number;
  demandaBateriaNecessaria: number;
}

export interface BessFaixaEnergia {
  pontaKwh: number;
  foraPontaKwh: number;
  totalKwh: number;
}

export interface BessBalancoEnergetico {
  consumo: {
    original: BessFaixaEnergia;
    ajustado: BessFaixaEnergia;
  };
  geracao: {
    original: BessFaixaEnergia;
    ajustado: BessFaixaEnergia;
  };
}

// Avisos do backend: informativos, nunca bloqueiam nem alteram o cálculo. A
// `mensagem` já vem formatada (valores e percentuais inclusos) e é exibida como
// veio — o `codigo` só decide qual campo do formulário destacar.
export interface BessAviso {
  codigo: string;
  mensagem: string;
}

// De onde veio a série horária. `mesReferencia` chega como 'MM/YYYY' — mesmo
// formato de BessParametros.mesReferencia, e diferente do 'YYYY-MM' enviado no
// payload.
export interface BessOrigemSerie {
  tipo: "MEDICAO" | "PERFIL";
  mesReferencia: string;
  perfil?: {
    codigo: PerfilConsumo;
    versao: number;
  };
}

export interface BessSimulationResult {
  parametros: BessParametros;
  horaria: BessHoraResultado[];
  diaria: BessResumoDiario[];
  resumo: BessResumoGeral;
  ajuste: BessAjuste;
  balancoEnergetico: BessBalancoEnergetico;
  avisos: BessAviso[];
  origemSerie: BessOrigemSerie;
}

// ─── Situação da usina e perfil de consumo ───────────────────────────────────

export type SituacaoUsina =
  | "NOVA"
  | "EXISTENTE"
  | "EXPANSAO"
  | "USINA_CONSORCIO";

export type PerfilConsumo =
  | "CONSTANTE_24X7"
  | "DIURNO_DIAS_UTEIS"
  | "ESTENDIDO_DIAS_UTEIS"
  | "CONTINUO_COM_REDUCAO_FIM_SEMANA"
  | "NOTURNO";

// Catálogo de GET /simulator/bess/profiles.
export interface BessProfile {
  codigo: PerfilConsumo;
  label: string;
  descricao: string;
}

// ─── Supporting data ─────────────────────────────────────────────────────────

export interface Distribuidora {
  id: string | number;
  name: string;
  uf: string;
  value: string;
  tarifas: string[];
}

export interface EnergyPriceCurve {
  i0Atacadista: string;
  i0Varejista: string;
  i5Atacadista: string;
  i5Varejista: string;
  i1Atacadista: string;
  i1Varejista: string;
}

export type EnergyPriceStructure = Record<number, EnergyPriceCurve>;

export interface EnergyPriceMatrix {
  norte: EnergyPriceStructure;
  nordeste: EnergyPriceStructure;
  sul: EnergyPriceStructure;
  sudesteCentroOeste: EnergyPriceStructure;
}

type SimulationHistoryValue =
  | string
  | number
  | boolean
  | string[]
  | null
  | undefined;

export interface SimulationHistoryItem {
  [key: string]: SimulationHistoryValue;
  schema?: string;
  partner_name?: string;
  nomeCliente?: string;
  cnpj?: string;
  simulationType?: SimulationType | string;
  simulation_date?: string;
  distribuidora?: string;
  ths?: string;
  grupoTarifario?: string;
  tipoContratacao?: string;
  desconto?: number;
  quantidadeAnos?: number;
  demandaForaPonta?: string;
  demandaPonta?: string;
  consumoForaPonta?: string;
  consumoPonta?: string;
  precoEnergia?: string[];
  precoEnergia1?: string;
  precoEnergia2?: string;
  precoEnergia3?: string;
  precoEnergia4?: string;
  precoEnergia5?: string;
  demandaUsina?: string;
  potenciaUsina?: string;
}

// ─── Simulation type enums ────────────────────────────────────────────────────

export type SimulationType =
  | "ML"
  | "APE"
  | "MLXAPE"
  | "GDXAPE"
  | "MLBESS"
  | "APEBESS";
export type SimulationSideType = "CATIVO" | "ML" | "APE" | "GD";

// MLBESS/APEBESS são comparações espelhadas: os dois lados usam o mesmo tipo de
// mercado (o backend isola o ganho da bateria pelo isBateria de cada payload).
export const SIMULATION_SIDE_TYPES: Record<
  SimulationType,
  { first: SimulationSideType; second: SimulationSideType }
> = {
  ML: { first: "CATIVO", second: "ML" },
  APE: { first: "CATIVO", second: "APE" },
  MLXAPE: { first: "ML", second: "APE" },
  GDXAPE: { first: "GD", second: "APE" },
  MLBESS: { first: "ML", second: "ML" },
  APEBESS: { first: "APE", second: "APE" },
};

export const SIMULATION_LABELS: Record<
  SimulationType,
  { first: string; second: string }
> = {
  ML: { first: "Consumidor Cativo", second: "Consumidor Livre" },
  APE: { first: "Consumidor Cativo", second: "Autoprodutor de Energia" },
  MLXAPE: { first: "Consumidor Livre", second: "Autoprodutor de Energia" },
  GDXAPE: { first: "Consumidor GD", second: "Autoprodutor de Energia" },
  MLBESS: {
    first: "Mercado Livre sem bateria",
    second: "Mercado Livre com bateria",
  },
  APEBESS: {
    first: "Autoprodutor sem bateria",
    second: "Autoprodutor com bateria",
  },
};

export const ECONOMY_CARD_LABELS: Record<
  SimulationType,
  { first: string; second: string }
> = {
  ML: { first: "Custo cativo", second: "Custo livre" },
  APE: { first: "Custo cativo", second: "Custo autoprodutor" },
  MLXAPE: { first: "Custo livre", second: "Custo autoprodutor" },
  GDXAPE: { first: "Custo GD", second: "Custo autoprodutor" },
  MLBESS: { first: "Custo sem bateria", second: "Custo com bateria" },
  APEBESS: { first: "Custo sem bateria", second: "Custo com bateria" },
};

// ─── Request payload ──────────────────────────────────────────────────────────

export interface SimulationPayloadItem {
  demandaForaPonta: number;
  demandaPonta: number;
  demandaPontaUltrapassagem: number;
  demandaForaPontaUltrapassagem: number;
  consumoPonta: number;
  consumoForaPonta: number;
  custoGestao: number;
  isGerador: boolean;
  consumoGerador: number;
  custoDiesel: number;
  custoGerador: number;
  isBateria: boolean;
  isDescontoGarantido: boolean;
  potenciaBateria: number;
  energiaMaximaArmazenada: number;
  investimentoBateria: number;
  rteBateria: number;
  distribuidora: string;
  ths: string;
  grupoTarifario: string;
  pis: number;
  cofins: number;
  icms: number;
  taxaAtratividade: number;
  reajusteDistribuidora: number;
  reajusteEnergia: number;
  initialYear: number;
  dataSimulacao: string;
  descontoTusdApe: boolean;
  precoEnergia: number[];
  desconto: number;
  isTaxCovid: boolean;
  isTaxScarcity: boolean;
  isEncargos: boolean;
  precoEnergiaGeracao: number[];
  local: boolean;
  abateICMS: boolean;
  // Só em APE/MLXAPE. Em GD → APE o backend infere o modo MIGRACAO_GD_APE pelo
  // par de tipos e ignora o valor, então nem enviamos.
  situacaoUsina?: SituacaoUsina;
  // Omitidos conforme a situação da usina — ver omitFieldsBySituacaoUsina.
  simultaneidade?: number;
  demandaUsina?: number;
  geracao?: number;
  custoProjeto?: number;
  medicaoCsv?: string;
  pontoMedicaoBess?: string;
  submercado?: BessSubmercado;
  dod?: number;
  // Modo sintético: exigidos quando há bateria e nenhuma medição. 'YYYY-MM'.
  mesReferencia?: string;
  perfilConsumo?: PerfilConsumo;
}

export interface SimulationRequest {
  firstSimulationType: SimulationSideType;
  secondSimulationType: SimulationSideType;
  firstPayload: SimulationPayloadItem;
  secondPayload: SimulationPayloadItem;
}

// ─── Formik values shape ──────────────────────────────────────────────────────

export interface FormValues {
  schema: string;
  simulationType: SimulationType;
  calc: string;
  distribuidora: string;
  uf: string;
  ths: string;
  grupoTarifario: string;
  demandaForaPonta: string;
  demandaForaPontaUltrapassagem: string;
  consumoForaPonta: string;
  consumoPonta: string;
  demandaPonta: string;
  demandaPontaUltrapassagem: string;
  desconto: number;
  tipoContratacao: string;
  precoPadrao: boolean;
  local: boolean;
  cnpj: string;
  pis: string;
  cofins: string;
  icms: string;
  geracao: string;
  demandaUsina: string;
  potenciaUsina: string;
  custoProjeto: string;
  custoGestao: string;
  nomeCliente: string;
  isTaxCovid: boolean;
  isTaxScarcity: boolean;
  isEncargos: boolean;
  abateICMS: boolean;
  situacaoUsina: SituacaoUsina;
  descontoTusdApe: boolean;
  simultaneidade: number;
  precoEnergiaGeracao: string[];
  precoEnergia: string[];
  isGerador: boolean;
  consumoGerador: string;
  custoDiesel: string;
  custoGerador: string;
  isBateria: boolean;
  isDescontoGarantido: boolean;
  potenciaBateria: string;
  energiaMaximaArmazenada: string;
  rteBateria: string;
  investimentoBateria: string;
  medicaoCsv: string;
  medicaoCsvFileName: string;
  submercado: string;
  dod: string;
  pontoMedicaoBess: string;
  mesReferencia: string; // 'YYYY-MM' — value nativo do <input type="month">
  perfilConsumo: PerfilConsumo | "";
  generateProposal: boolean;
  taxaAtratividade: string;
  reajusteDistribuidora: string;
  reajusteEnergia: string;
  lcoe: string;
  clientLogo: File | null;
  partnerLogo: File | null;
  projectImage1: File | null;
  projectImage2: File | null;
  localization: string;
  modules: string;
  inverter: string;
  structure: string;
  items: Array<{
    nome: string;
    potencia: string;
    geradorFotovoltaico: string;
    projetoHomologacao: string;
    epc: string;
  }>;
  initialYear: number;
  quantidadeAnos: number;
}
